import { CELOSIAS_EDITORIAL_COVER_URL } from "../lib/utils";
import { getCollectionLabels, getCollectionNaming } from "./collectionNaming";

export const DEFAULT_HOME_ASSETS = {
  brandEditorialImage: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/hrq6uxdx_T060.jpeg",
  customizationImage: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/p7bjah4a_7C55E21A-BBF9-45DF-9462-2CCCAF5E044A.jpg",
};

const cloneValue = (value) => {
  if (value === undefined) {
    return undefined;
  }
  return JSON.parse(JSON.stringify(value));
};

const isPlainObject = (value) => Boolean(value) && Object.prototype.toString.call(value) === "[object Object]";

export const mergeLocalizedOverride = (baseValue, overrideValue) => {
  if (Array.isArray(overrideValue)) {
    return cloneValue(overrideValue);
  }

  if (isPlainObject(baseValue) && isPlainObject(overrideValue)) {
    return Object.keys(overrideValue).reduce(
      (accumulator, key) => ({
        ...accumulator,
        [key]: mergeLocalizedOverride(baseValue[key], overrideValue[key]),
      }),
      { ...baseValue },
    );
  }

  if (isPlainObject(overrideValue)) {
    return cloneValue(overrideValue);
  }

  return overrideValue !== undefined ? overrideValue : cloneValue(baseValue);
};

export const LOCALE_OPTIONS = [
  { code: "es-MX", label: "México" },
  { code: "es-ES", label: "España" },
  { code: "en-US", label: "United States" },
];

export const detectLocale = () => {
  if (typeof navigator === "undefined") {
    return "es-MX";
  }

  const language = navigator.language.toLowerCase();

  if (language.startsWith("es-es")) {
    return "es-ES";
  }

  if (language.startsWith("en-us") || language.startsWith("en")) {
    return "en-US";
  }

  return "es-MX";
};

const uiByLocale = {
  "es-MX": {
    app: {
      loadingTitle: "Preparando una experiencia material precisa.",
      errorTitle: "No pudimos cargar el contenido del sitio por el momento.",
    },
    navigation: ["Colecciones", "Catálogos", "Texturas", "Colores", "Manuales", "Aliados", "Contacto"],
    layout: {
      headerContact: "Contactar",
      regionLabel: "Región",
      footerBrandTitle: "Concreto arquitectónico con presencia escultórica y precisión material.",
      contactLabel: "Contacto",
      socialLabel: "Social",
      contactPageLabel: "Ir a contacto",
      legalTitle: "Aviso de privacidad, políticas de compra / póliza de garantía",
    },
    home: {
      heroBadge: "¡Atrévete a innovar!",
      brandFeatureTitle: ["Diseñamos espacios que", "generan emociones."],
      brandSectionTitle: "Marca",
      clientsKicker: "Clientes destacados",
      clientsLink: "Ver aliados",
      collectionsTitle: "Colecciones atemporales y perfectas para todo tipo de espacios.",
      customizationProcessLabel: "Proceso de pedido",
      customizationStepsCount: "4 pasos",
      catalogsTitle: ["Explora nuestros catálogos", "e inspírate para tu proyecto"],
      catalogsDescription:
        "Descarga nuestros catálogos, cada colección se encuentra inspirada para que se adapte perfectamente en todo tipo de proyectos.",
      catalogsCta: "Ver todos los catálogos",
      showroomKicker: "Showroom",
      showroomMap: "Abrir ubicación",
      showroomContact: "Agendar visita",
      instagramTitle: ["Síguenos y mantente", "al tanto de las tendencias"],
      instagramDescription:
        "Descubre inspiración, aplicaciones reales y detalles de producto directamente desde el Instagram de la marca.",
      contactKicker: "Contacto",
      contactTitle: "Agenda una conversación y definamos la mejor solución para tu proyecto.",
      contactDescription: "",
      contactPrimary: "WhatsApp ventas",
      contactSecondary: "Ir a contacto",
    },
    collectionsPage: {
      kicker: "Colecciones",
      title: "Colecciones Arista + Vértice 2026.",
      description:
        "Encuentra la tendencia perfecta para cualquier tipo de proyectos.",
      familyLabel: "Familia",
      referenceLink: "Ver referencia actual",
    },
    detailPage: {
      catalogs: {
        kicker: "Catálogos",
        title: "Descarga nuestras colecciones para seguir inspirándote en tus proyectos.",
        description:
          "Una vista dedicada a colecciones y referencias para despachos, desarrolladores y clientes finales.",
      },
      textures: {
        kicker: "Texturas",
        title: "Superficies con intención, profundidad y acabados de alto detalle.",
        description:
          "La personalización en texturas permite llevar cada proyecto desde una lectura serena hasta una presencia escultórica.",
      },
      colors: {
        kicker: "Colores",
        title: "Paleta mineral para proyectos sobrios y sofisticados.",
        description:
          "Los tonos funcionan como punto de partida para una conversación más amplia sobre acabados y atmósfera.",
      },
      manuals: {
        kicker: "Manuales",
        title: "Conoce nuestros manuales de descarga y los cuidados antes, durante y después de instalar.",
        description: "¿Tienes alguna duda? Contacta a tu asesor y con gusto te ayudaremos.",
      },
      allies: {
        kicker: "Aliados",
        title: "Una marca construida junto a arquitectos e interioristas.",
        description:
          "La página de aliados comunica confianza, respaldo comercial y un historial visual con clientes destacados.",
        pillarLabel: "Valor",
      },
      contact: {
        kicker: "Contacto",
        title: "Showroom, contacto regional y solicitud de información.",
        description:
          "Todo el acceso comercial en un solo lugar: canal regional, correo, dirección y formulario de proyecto.",
        showroom: "Showroom",
        office: "Oficina",
        whatsapp: "WhatsApp",
        primaryChannel: "Canal principal",
        responseTime: "Atención",
        mainEmail: "Correo principal",
        map: "Mapa",
        mapLabel: "Abrir ubicación del showroom",
      },
      cardSectionLabels: {
        catalogs: "Catálogos",
        textures: "Texturas",
        manuals: "Manuales",
      },
    },
    form: {
      name: "Nombre",
      email: "Correo",
      phone: "Teléfono",
      interest: "Interés principal",
      message: "Cuéntanos tu proyecto",
      submit: "Solicitar información",
      sending: "Enviando...",
      success: "Tu solicitud fue enviada correctamente.",
      error: "No pudimos enviar tu solicitud. Intenta nuevamente.",
      regionRoutingLabel: "Atención regional",
      primaryChannelLabel: "Canal principal",
    },
  },
  "es-ES": {
    app: {
      loadingTitle: "Preparando una experiencia material precisa.",
      errorTitle: "No hemos podido cargar el contenido del sitio por el momento.",
    },
    navigation: ["Colecciones", "Catálogos", "Texturas", "Colores", "Manuales", "Aliados", "Contacto"],
    layout: {
      headerContact: "Contactar",
      regionLabel: "Región",
      footerBrandTitle: "Hormigón arquitectónico con presencia escultórica y precisión material.",
      contactLabel: "Contacto",
      socialLabel: "Social",
      contactPageLabel: "Ir a contacto",
      legalTitle: "Aviso de privacidad, políticas de compra / póliza de garantía",
    },
    home: {
      heroBadge: "¡Atrévete a innovar!",
      brandFeatureTitle: ["Diseñamos espacios que", "generan emociones."],
      brandSectionTitle: "Marca",
      clientsKicker: "Clientes destacados",
      clientsLink: "Ver aliados",
      collectionsTitle: "Colecciones atemporales y perfectas para todo tipo de espacios.",
      customizationProcessLabel: "Proceso del pedido",
      customizationStepsCount: "4 pasos",
      catalogsTitle: ["Explora nuestros catálogos", "e inspírate para tu proyecto"],
      catalogsDescription:
        "Descarga nuestros catálogos; cada colección está pensada para adaptarse perfectamente a todo tipo de proyectos.",
      catalogsCta: "Ver todos los catálogos",
      showroomKicker: "Showroom",
      showroomMap: "Ver ubicación",
      showroomContact: "Hablar con asesor",
      instagramTitle: ["Síguenos y mantente", "al tanto de las tendencias"],
      instagramDescription:
        "Descubre inspiración, aplicaciones reales y detalles de producto directamente desde el Instagram de la marca.",
      contactKicker: "Contacto",
      contactTitle: "Conversemos y definamos la mejor solución para tu proyecto.",
      contactDescription: "",
      contactPrimary: "WhatsApp",
      contactSecondary: "Ir a contacto",
    },
    collectionsPage: {
      kicker: "Colecciones",
      title: "El sistema material de Arista + Vértice.",
      description:
        "Una página dedicada a las familias principales de producto para facilitar la exploración visual y la especificación por tipo de aplicación.",
      familyLabel: "Familia",
      referenceLink: "Ver referencia actual",
    },
    detailPage: {
      catalogs: {
        kicker: "Catálogos",
        title: "Descarga nuestras colecciones para seguir inspirándote en tus proyectos.",
        description: "Una vista dedicada a colecciones y referencias para estudios, promotores y clientes finales.",
      },
      textures: {
        kicker: "Texturas",
        title: "Superficies con intención, profundidad y acabados de alto detalle.",
        description:
          "La personalización en texturas permite llevar cada proyecto desde una lectura serena hasta una presencia escultórica.",
      },
      colors: {
        kicker: "Colores",
        title: "Paleta mineral para proyectos sobrios y sofisticados.",
        description: "Los tonos funcionan como punto de partida para una conversación más amplia sobre acabados y atmósfera.",
      },
      manuals: {
        kicker: "Manuales",
        title: "Conoce nuestros manuales de descarga y los cuidados antes, durante y después de instalar.",
        description: "¿Tienes alguna duda? Contacta a tu asesor y con gusto te ayudaremos.",
      },
      allies: {
        kicker: "Aliados",
        title: "Una marca construida junto a arquitectos e interioristas.",
        description: "La página de aliados comunica confianza, respaldo comercial y un historial visual con clientes destacados.",
        pillarLabel: "Valor",
      },
      contact: {
        kicker: "Contacto",
        title: "Showroom, contacto regional y solicitud de información.",
        description: "Todo el acceso comercial en un solo lugar: canal regional, correo, dirección y formulario de proyecto.",
        showroom: "Showroom",
        office: "Oficina",
        whatsapp: "WhatsApp",
        primaryChannel: "Canal principal",
        responseTime: "Atención",
        mainEmail: "Correo principal",
        map: "Mapa",
        mapLabel: "Abrir ubicación del showroom",
      },
      cardSectionLabels: {
        catalogs: "Catálogos",
        textures: "Texturas",
        manuals: "Manuales",
      },
    },
    form: {
      name: "Nombre",
      email: "Correo",
      phone: "Teléfono",
      interest: "Interés principal",
      message: "Cuéntanos tu proyecto",
      submit: "Solicitar información",
      sending: "Enviando...",
      success: "Tu solicitud se ha enviado correctamente.",
      error: "No hemos podido enviar tu solicitud. Inténtalo de nuevo.",
      regionRoutingLabel: "Atención regional",
      primaryChannelLabel: "Canal principal",
    },
  },
  "en-US": {
    app: {
      loadingTitle: "Preparing a precise material experience.",
      errorTitle: "We couldn't load the site content right now.",
    },
    navigation: ["Collections", "Catalogs", "Textures", "Colors", "Manuals", "Allies", "Contact"],
    layout: {
      headerContact: "Contact us",
      regionLabel: "Region",
      footerBrandTitle: "Architectural concrete with sculptural presence and material precision.",
      contactLabel: "Contact",
      socialLabel: "Social",
      contactPageLabel: "Go to contact",
      legalTitle: "Privacy notice, purchase policies / warranty policy",
    },
    home: {
      heroBadge: "Dare to innovate!",
      brandFeatureTitle: ["We design spaces that", "create emotion."],
      brandSectionTitle: "Brand",
      clientsKicker: "Featured clients",
      clientsLink: "View allies",
      collectionsTitle: "Timeless collections designed to work across all kinds of spaces.",
      customizationProcessLabel: "Order process",
      customizationStepsCount: "4 steps",
      catalogsTitle: ["Explore our catalogs", "and get inspired for your project"],
      catalogsDescription:
        "Download our catalogs. Each collection is thoughtfully created to fit beautifully into all kinds of projects.",
      catalogsCta: "View all catalogs",
      showroomKicker: "Showroom",
      showroomMap: "Open location",
      showroomContact: "Talk to our team",
      instagramTitle: ["Follow us and stay", "on top of the trends"],
      instagramDescription: "Explore inspiration, real applications and product details directly from the brand's Instagram feed.",
      contactKicker: "Contact",
      contactTitle: "Let's define the right solution for your project together.",
      contactDescription: "",
      contactPrimary: "Get in touch",
      contactSecondary: "Go to contact",
    },
    collectionsPage: {
      kicker: "Collections",
      title: "The material system of Arista + Vértice.",
      description:
        "A dedicated page for the core product families, making visual exploration and specification by application easier.",
      familyLabel: "Family",
      referenceLink: "View current reference",
    },
    detailPage: {
      catalogs: {
        kicker: "Catalogs",
        title: "Download our collections to keep inspiring your projects.",
        description: "A dedicated view for collections and references for studios, developers and end clients.",
      },
      textures: {
        kicker: "Textures",
        title: "Surfaces with intention, depth and high-detail finishes.",
        description: "Texture personalization lets each project move from a calm reading to a sculptural presence.",
      },
      colors: {
        kicker: "Colors",
        title: "A mineral palette for refined and sophisticated projects.",
        description: "These tones serve as the starting point for a broader conversation around atmosphere and finish.",
      },
      manuals: {
        kicker: "Manuals",
        title: "Documentation created to specify better.",
        description: "Installation, maintenance and technical specifications presented through a clean and direct navigation.",
      },
      allies: {
        kicker: "Allies",
        title: "A brand built alongside architects and interior designers.",
        description: "This page communicates trust, commercial support and a visual history with featured clients.",
        pillarLabel: "Value",
      },
      contact: {
        kicker: "Contact",
        title: "Regional contact, support and project inquiry.",
        description: "All commercial access in one place: your regional channel, email, address and project inquiry form.",
        showroom: "Showroom",
        office: "Office",
        whatsapp: "WhatsApp",
        primaryChannel: "Primary channel",
        responseTime: "Availability",
        mainEmail: "Main email",
        map: "Map",
        mapLabel: "Open showroom location",
      },
      cardSectionLabels: {
        catalogs: "Catalogs",
        textures: "Textures",
        manuals: "Manuals",
      },
    },
    form: {
      name: "Name",
      email: "Email",
      phone: "Phone",
      interest: "Main interest",
      message: "Tell us about your project",
      submit: "Request information",
      sending: "Sending...",
      success: "Your request was sent successfully.",
      error: "We couldn't send your request. Please try again.",
      regionRoutingLabel: "Regional routing",
      primaryChannelLabel: "Primary channel",
    },
  },
};

const legalParagraphsByLocale = {
  "es-MX": [
    "© 2026, ARISTA + VÉRTICE ®. Todos los Derechos Reservados. Registros ante el Instituto de Propiedad Intelectual 2246360, 2226944, 2226945, 129574, 2104118, 2246358.",
    "Se prohíbe la reproducción parcial o total de la información aquí presentada. Así mismo se prohíbe el uso no autorizado y/o modificación total o parcial de nuestras colecciones.",
    "¡Atrévete a Innovar!® y todas las marcas y/o colecciones son propiedad de Arista más Vértice S. de R.L. de C.V.",
  ],
  "es-ES": [
    "© 2026, ARISTA + VÉRTICE ®. Todos los Derechos Reservados. Registros ante el Instituto de Propiedad Intelectual 2246360, 2226944, 2226945, 129574, 2104118, 2246358.",
    "Se prohíbe la reproducción parcial o total de la información aquí presentada. Así mismo se prohíbe el uso no autorizado y/o modificación total o parcial de nuestras colecciones.",
    "¡Atrévete a Innovar!® y todas las marcas y/o colecciones son propiedad de Arista más Vértice S. de R.L. de C.V.",
  ],
  "en-US": [
    "© 2026, ARISTA + VÉRTICE ®. All rights reserved. Registrations before the Intellectual Property Institute 2246360, 2226944, 2226945, 129574, 2104118, 2246358.",
    "Partial or total reproduction of the information presented here is prohibited. Unauthorized use and/or total or partial modification of our collections is also prohibited.",
    "Dare to Innovate!® and all marks and/or collections are property of Arista más Vértice S. de R.L. de C.V.",
  ],
};

const uiExtensionsByLocale = {
  "es-MX": {
    home: {
      collectionsKicker: "Colecciones",
      collectionLinkLabel: "Explorar colección",
      customizationKicker: "Personalización",
      configuratorCta: "Abrir configurador",
      customizationLinks: {
        design: "Ver diseños",
        color: "Ver colores",
        textures: "Ver texturas",
        finish: "Ver acabados",
      },
      catalogsKicker: "Catálogos",
      manualsKicker: "Manuales",
      manualsCta: "Ver manuales",
      instagramKicker: "Instagram",
    },
    collectionsPage: {
      detailLink: "Explorar colección",
    },
    collectionDetailPage: {
      kicker: "Colección",
      backLink: "Volver a colecciones",
      applicationsLabel: "Aplicaciones recomendadas",
      highlightsLabel: "Claves de especificación",
      contactCta: "Solicitar asesoría",
      referenceCta: "Ver colección completa",
      collections: {
        celosias: {
          heroImage: CELOSIAS_EDITORIAL_COVER_URL,
          intro: "Celosías concebidas para filtrar la luz, ordenar visualmente una fachada y crear privacidad con una lectura escultórica.",
          highlights: [
            "Aportan ritmo arquitectónico a fachadas, patios y transiciones.",
            "Permiten modular sombra, vistas y ventilación con un gesto limpio.",
            "Funcionan especialmente bien en proyectos residenciales, hospitality y uso mixto.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Luz y privacidad en equilibrio",
              description: "Su lenguaje material permite controlar vistas y entradas de luz sin perder continuidad ni elegancia en el conjunto arquitectónico.",
            },
            {
              label: "Aplicación",
              title: "Fachadas y transiciones con carácter",
              description: "Es una colección pensada para zonas de transición, filtros exteriores y frentes que necesitan identidad sin sobrecargar la arquitectura.",
            },
            {
              label: "Personalización",
              title: "Patrón, tono y profundidad",
              description: "Puede especificarse con diferentes lecturas de color y textura para integrarse a un lenguaje más sereno o más expresivo.",
            },
          ],
        },
        muro: {
          intro: "Superficies para muro pensadas para crear atmósferas que transmitan emociones.",
          highlights: [
            "Construye muros acento con continuidad y textura controlada.",
            "Responde bien en interiores residenciales, hospitality y espacios comerciales.",
            "Su lectura calmada permite combinarse con madera, piedra y metal.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Una base material serena",
              description: "La colección Muro está pensada para generar profundidad visual sin competir con el mobiliario o la arquitectura principal del espacio.",
            },
            {
              label: "Aplicación",
              title: "Interiorismo y muros protagonistas",
              description: "Funciona especialmente bien en vestíbulos, áreas sociales, recámaras, retail y proyectos donde el muro debe sostener la atmósfera del lugar.",
            },
            {
              label: "Personalización",
              title: "Texturas discretas, resultados precisos",
              description: "El sistema admite ajustes de color, acabado y lectura superficial para que la pieza final dialogue con cada proyecto de forma natural.",
            },
          ],
        },
        paneles: {
          intro: "Paneles de gran presencia visual para construir superficies limpias, continuas y decididamente contemporáneas.",
          highlights: [
            "Formatos protagonistas para lobbies, retail y zonas de alto impacto visual.",
            "La modulación del panel ayuda a ordenar superficies amplias con claridad.",
            "Combina una lectura premium con soluciones de especificación versátiles.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Superficies amplias y contundentes",
              description: "Paneles responde a proyectos que necesitan una lectura monolítica y limpia, con un material que mantenga detalle incluso a gran escala.",
            },
            {
              label: "Aplicación",
              title: "Espacios comerciales y residenciales premium",
              description: "Es ideal para frentes interiores, áreas de recepción, muros de marca y composiciones donde el formato se convierte en parte del lenguaje espacial.",
            },
            {
              label: "Personalización",
              title: "Formato, tono y presencia",
              description: "La colección puede ajustarse para lograr composiciones más discretas o más gráficas, según la dirección visual del proyecto.",
            },
          ],
        },
        pisos: {
          intro: "Soluciones para piso con apariencia monolítica, desempeño confiable y una expresión material sobria.",
          highlights: [
            "Pensado para zonas de tránsito frecuente y ambientes premium.",
            "Su estética continua ayuda a ampliar visualmente los espacios.",
            "Puede especificarse en tonos cálidos, neutros o más profundos según el proyecto.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Base silenciosa para el espacio",
              description: "Pisos ofrece una lectura uniforme y arquitectónica para proyectos que buscan continuidad, orden y una sofisticación material discreta.",
            },
            {
              label: "Aplicación",
              title: "Residencial, hospitality y comercio",
              description: "Se adapta a proyectos donde la resistencia y la limpieza visual deben convivir con una experiencia premium del usuario final.",
            },
            {
              label: "Personalización",
              title: "Color y acabado según uso",
              description: "El acabado puede ajustarse para responder tanto a criterios estéticos como a necesidades operativas del entorno.",
            },
          ],
        },
      },
    },
  },
  "es-ES": {
    home: {
      collectionsKicker: "Colecciones",
      collectionLinkLabel: "Explorar colección",
      customizationKicker: "Personalización",
      configuratorCta: "Abrir configurador",
      customizationLinks: {
        design: "Ver diseños",
        color: "Ver colores",
        textures: "Ver texturas",
        finish: "Ver acabados",
      },
      catalogsKicker: "Catálogos",
      manualsKicker: "Manuales",
      manualsCta: "Ver manuales",
      instagramKicker: "Instagram",
    },
    collectionsPage: {
      detailLink: "Explorar colección",
    },
    collectionDetailPage: {
      kicker: "Colección",
      backLink: "Volver a colecciones",
      applicationsLabel: "Aplicaciones recomendadas",
      highlightsLabel: "Claves de especificación",
      contactCta: "Solicitar asesoramiento",
      referenceCta: "Ver colección completa",
      collections: {
        celosias: {
          heroImage: CELOSIAS_EDITORIAL_COVER_URL,
          intro: "Celosías concebidas para filtrar la luz, ordenar visualmente una fachada y crear privacidad con una lectura escultórica.",
          highlights: [
            "Aportan ritmo arquitectónico a fachadas, patios y transiciones.",
            "Permiten modular sombra, vistas y ventilación con un gesto limpio.",
            "Funcionan especialmente bien en proyectos residenciales, hospitality y uso mixto.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Luz y privacidad en equilibrio",
              description: "Su lenguaje material permite controlar vistas y entradas de luz sin perder continuidad ni elegancia en el conjunto arquitectónico.",
            },
            {
              label: "Aplicación",
              title: "Fachadas y transiciones con carácter",
              description: "Es una colección pensada para zonas de transición, filtros exteriores y frentes que necesitan identidad sin sobrecargar la arquitectura.",
            },
            {
              label: "Personalización",
              title: "Patrón, tono y profundidad",
              description: "Puede especificarse con distintas lecturas de color y textura para integrarse en un lenguaje más sereno o más expresivo.",
            },
          ],
        },
        muro: {
          intro: "Superficies para muro pensadas para crear atmósferas que transmitan emociones.",
          highlights: [
            "Construye muros acento con continuidad y textura controlada.",
            "Responde bien en interiores residenciales, hospitality y espacios comerciales.",
            "Su lectura calmada permite combinarse con madera, piedra y metal.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Una base material serena",
              description: "La colección Muro está pensada para generar profundidad visual sin competir con el mobiliario o la arquitectura principal del espacio.",
            },
            {
              label: "Aplicación",
              title: "Interiorismo y muros protagonistas",
              description: "Funciona especialmente bien en vestíbulos, zonas sociales, dormitorios, retail y proyectos donde el muro debe sostener la atmósfera del lugar.",
            },
            {
              label: "Personalización",
              title: "Texturas discretas, resultados precisos",
              description: "El sistema admite ajustes de color, acabado y lectura superficial para que la pieza final dialogue con cada proyecto de forma natural.",
            },
          ],
        },
        paneles: {
          intro: "Paneles de gran presencia visual para construir superficies limpias, continuas y decididamente contemporáneas.",
          highlights: [
            "Formatos protagonistas para lobbies, retail y zonas de alto impacto visual.",
            "La modulación del panel ayuda a ordenar superficies amplias con claridad.",
            "Combina una lectura premium con soluciones de especificación versátiles.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Superficies amplias y contundentes",
              description: "Paneles responde a proyectos que necesitan una lectura monolítica y limpia, con un material que mantenga detalle incluso a gran escala.",
            },
            {
              label: "Aplicación",
              title: "Espacios comerciales y residenciales premium",
              description: "Es ideal para frentes interiores, áreas de recepción, muros de marca y composiciones donde el formato se convierte en parte del lenguaje espacial.",
            },
            {
              label: "Personalización",
              title: "Formato, tono y presencia",
              description: "La colección puede ajustarse para lograr composiciones más discretas o más gráficas, según la dirección visual del proyecto.",
            },
          ],
        },
        pisos: {
          intro: "Soluciones para pavimento con apariencia monolítica, rendimiento fiable y una expresión material sobria.",
          highlights: [
            "Pensado para zonas de tránsito frecuente y ambientes premium.",
            "Su estética continua ayuda a ampliar visualmente los espacios.",
            "Puede especificarse en tonos cálidos, neutros o más profundos según el proyecto.",
          ],
          editorialBlocks: [
            {
              label: "Intención",
              title: "Base silenciosa para el espacio",
              description: "Pisos ofrece una lectura uniforme y arquitectónica para proyectos que buscan continuidad, orden y una sofisticación material discreta.",
            },
            {
              label: "Aplicación",
              title: "Residencial, hospitality y comercio",
              description: "Se adapta a proyectos donde la resistencia y la limpieza visual deben convivir con una experiencia premium del usuario final.",
            },
            {
              label: "Personalización",
              title: "Color y acabado según uso",
              description: "El acabado puede ajustarse para responder tanto a criterios estéticos como a necesidades operativas del entorno.",
            },
          ],
        },
      },
    },
  },
  "en-US": {
    home: {
      collectionsKicker: "Collections",
      collectionLinkLabel: "Explore collection",
      customizationKicker: "Customization",
      configuratorCta: "Open configurator",
      customizationLinks: {
        design: "View designs",
        color: "View colors",
        textures: "View textures",
        finish: "View finishes",
      },
      catalogsKicker: "Catalogs",
      manualsKicker: "Manuals",
      manualsCta: "View manuals",
      instagramKicker: "Instagram",
    },
    collectionsPage: {
      detailLink: "Explore collection",
    },
    collectionDetailPage: {
      kicker: "Collection",
      backLink: "Back to collections",
      applicationsLabel: "Recommended applications",
      highlightsLabel: "Specification highlights",
      contactCta: "Request guidance",
      referenceCta: "View full collection",
      collections: {
        celosias: {
          heroImage: CELOSIAS_EDITORIAL_COVER_URL,
          intro: `Architectural ${getCollectionNaming("en-US", "celosias").designPluralLabel} designed to filter light, organize façades visually and create privacy with a sculptural reading.`,
          highlights: [
            "Adds architectural rhythm to façades, courtyards and transition zones.",
            "Modulates shade, views and ventilation through a clean material gesture.",
            "Works especially well for residential, hospitality and mixed-use projects.",
          ],
          editorialBlocks: [
            {
              label: "Intent",
              title: "Light and privacy in balance",
              description: "Its material language helps control views and daylight while preserving continuity and elegance across the architectural envelope.",
            },
            {
              label: "Application",
              title: "Façades and transitions with character",
              description: `${getCollectionNaming("en-US", "celosias").title} are ideal for exterior filters, transition areas and frontages that need identity without visual excess.`,
            },
            {
              label: "Customization",
              title: "Pattern, tone and depth",
              description: "It can be specified through different color and texture readings to support either a calm or more expressive visual direction.",
            },
          ],
        },
        muro: {
          intro: "Wall surfaces designed to create atmospheres that transmit emotion.",
          highlights: [
            "Builds accent walls with continuity and controlled texture.",
            "Responds well in residential interiors, hospitality and commercial spaces.",
            "Its calm reading pairs naturally with wood, stone and metal.",
          ],
          editorialBlocks: [
            {
              label: "Intent",
              title: "A serene material base",
              description: `${getCollectionNaming("en-US", "muro").title} are built to create visual depth without competing with furniture or the core architecture of the space.`,
            },
            {
              label: "Application",
              title: "Interior design and hero walls",
              description: "It performs beautifully in lobbies, social spaces, bedrooms, retail environments and projects where the wall needs to carry the atmosphere.",
            },
            {
              label: "Customization",
              title: "Quiet textures, precise outcomes",
              description: "The system allows adjustments in color, finish and surface reading so the final piece feels native to every project.",
            },
          ],
        },
        paneles: {
          intro: "Large-format panels created for clean, continuous and decisively contemporary surfaces.",
          highlights: [
            "A statement solution for lobbies, retail and high-impact visual zones.",
            "The panel rhythm helps organize expansive surfaces with clarity.",
            "Combines premium material presence with flexible specification paths.",
          ],
          editorialBlocks: [
            {
              label: "Intent",
              title: "Broad surfaces with confidence",
              description: `${getCollectionNaming("en-US", "paneles").title} are meant for projects that need a monolithic, refined reading supported by a material that still retains detail at scale.`,
            },
            {
              label: "Application",
              title: "Premium commercial and residential spaces",
              description: "It is especially effective for interior frontages, reception zones, brand walls and compositions where format becomes part of the spatial language.",
            },
            {
              label: "Customization",
              title: "Format, tone and visual weight",
              description: "The collection can be tuned toward quieter or more graphic compositions depending on the direction of the project.",
            },
          ],
        },
        pisos: {
          intro: "Floor solutions with a monolithic appearance, reliable performance and a restrained material expression.",
          highlights: [
            "Designed for frequent traffic and premium environments.",
            "Its continuous aesthetic helps spaces feel broader and more composed.",
            "Can be specified in warm, neutral or deeper tones depending on the project.",
          ],
          editorialBlocks: [
            {
              label: "Intent",
              title: "A quiet base for the space",
              description: "Floors offers a uniform architectural reading for projects that seek continuity, order and discreet material sophistication.",
            },
            {
              label: "Application",
              title: "Residential, hospitality and commercial",
              description: "It adapts to projects where durability and visual cleanliness must coexist with a premium end-user experience.",
            },
            {
              label: "Customization",
              title: "Color and finish by use case",
              description: "The finish can be adjusted to respond both to aesthetic goals and to operational needs of the environment.",
            },
          ],
        },
      },
    },
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].layout.legalParagraphs = legalParagraphsByLocale[locale];
  uiByLocale[locale].home = {
    ...uiByLocale[locale].home,
    ...(uiExtensionsByLocale[locale]?.home || {}),
  };
  uiByLocale[locale].collectionsPage = {
    ...uiByLocale[locale].collectionsPage,
    ...(uiExtensionsByLocale[locale]?.collectionsPage || {}),
  };
  uiByLocale[locale].collectionDetailPage = uiExtensionsByLocale[locale]?.collectionDetailPage;
});

const resourcesUiByLocale = {
  "es-MX": {
    typeLabel: "Tipo",
    collectionLabel: "Colección",
    manualCategoryLabel: "Categoría",
    allCollectionsLabel: "Todas",
    previewTitle: "Vista previa",
    previewDescription: "Selecciona un recurso para verlo aquí mismo antes de descargarlo.",
    previewButton: "Vista previa",
    downloadButton: "Descargar",
    openButton: "Abrir PDF",
    noResults: "No hay recursos para este filtro todavía.",
    temporaryLabel: "Documento PDF",
    typeTabs: {
      catalogs: "Catálogos",
      manuals: "Manuales",
    },
    manualCategories: {
      handling: "Manejo/Descarga",
      installation: "Instalación y Mantenimiento",
    },
    defaultManualCategory: "installation",
    defaultManualId: "manual-instalacion-losetas",
    manualItemOverrides: {
      "manual-descarga-general": {
        title: "Manual de descarga de losetas",
        description: "Guía base para descarga, maniobra y recepción correcta de losetas en obra.",
        category: "handling",
      },
      "manual-instalacion-losetas": {
        title: "Instalación de pisos",
        description: "Guía base para instalación de pisos y buenas prácticas de aplicación en obra.",
        category: "installation",
      },
      "manual-limpieza-mantenimiento": {
        category: "installation",
      },
      "manual-descarga-paneles": {
        category: "handling",
      },
      "manual-descarga-celosias-paneles": {
        category: "handling",
      },
      "manual-instalacion-celosias": {
        category: "installation",
      },
      "manual-instalacion-losetas-paneles": {
        title: "Instalación de losetas y paneles",
        description: "Guía de instalación para losetas y paneles con recomendaciones de aplicación y montaje en obra.",
        category: "installation",
      },
    },
    collectionLabels: getCollectionLabels("es-MX"),
  },
  "es-ES": {
    typeLabel: "Tipo",
    collectionLabel: "Colección",
    manualCategoryLabel: "Categoría",
    allCollectionsLabel: "Todas",
    previewTitle: "Vista previa",
    previewDescription: "Selecciona un recurso para verlo aquí mismo antes de descargarlo.",
    previewButton: "Vista previa",
    downloadButton: "Descargar",
    openButton: "Abrir PDF",
    noResults: "Todavía no hay recursos para este filtro.",
    temporaryLabel: "Documento PDF",
    typeTabs: {
      catalogs: "Catálogos",
      manuals: "Manuales",
    },
    manualCategories: {
      handling: "Manejo/Descarga",
      installation: "Instalación y Mantenimiento",
    },
    defaultManualCategory: "installation",
    defaultManualId: "manual-instalacion-losetas",
    manualItemOverrides: {
      "manual-descarga-general": {
        title: "Manual de descarga de losetas",
        description: "Guía base para descarga, maniobra y recepción correcta de losetas en obra.",
        category: "handling",
      },
      "manual-instalacion-losetas": {
        title: "Instalación de pisos",
        description: "Guía base para instalación de pisos y buenas prácticas de aplicación en obra.",
        category: "installation",
      },
      "manual-limpieza-mantenimiento": {
        category: "installation",
      },
      "manual-descarga-paneles": {
        category: "handling",
      },
      "manual-descarga-celosias-paneles": {
        category: "handling",
      },
      "manual-instalacion-celosias": {
        category: "installation",
      },
      "manual-instalacion-losetas-paneles": {
        title: "Instalación de losetas y paneles",
        description: "Guía de instalación para losetas y paneles con recomendaciones de aplicación y montaje en obra.",
        category: "installation",
      },
    },
    collectionLabels: getCollectionLabels("es-ES"),
  },
  "en-US": {
    typeLabel: "Type",
    collectionLabel: "Collection",
    manualCategoryLabel: "Category",
    allCollectionsLabel: "All",
    previewTitle: "Preview",
    previewDescription: "Select a resource to preview it here before downloading it.",
    previewButton: "Preview",
    downloadButton: "Download",
    openButton: "Open PDF",
    noResults: "There are no resources for this filter yet.",
    temporaryLabel: "PDF document",
    typeTabs: {
      catalogs: "Catalogs",
      manuals: "Manuals",
    },
    manualCategories: {
      handling: "Handling / Unloading",
      installation: "Installation and Maintenance",
    },
    defaultManualCategory: "installation",
    defaultManualId: "manual-instalacion-losetas",
    manualItemOverrides: {
      "manual-descarga-general": {
        category: "handling",
      },
      "manual-instalacion-losetas": {
        category: "installation",
      },
      "manual-limpieza-mantenimiento": {
        category: "installation",
      },
      "manual-descarga-paneles": {
        category: "handling",
      },
      "manual-descarga-celosias-paneles": {
        category: "handling",
      },
      "manual-instalacion-celosias": {
        category: "installation",
      },
    },
    collectionLabels: getCollectionLabels("en-US"),
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].resourcesPage = resourcesUiByLocale[locale];
});

const colorsPageMetaByLocale = {
  "es-MX": {
    lineSection: "Colores de línea",
    metallicSection: "Metálicos",
    variationNote: "Debido a su proceso de fabricación neoartesanal las tonalidades expresadas pueden tener variaciones.",
    paletteNote: "Código numérico correspondendiente a la paleta ColorLife® 2.0 de la marca Comex.",
    filterLabel: "Familias",
    selectedLabel: "Selección actual",
    visibleLabel: "tonos visibles",
    paletteLabel: "Referencia de paleta",
    variationLabel: "Variación de tono",
    applicationsLabel: "Información importante",
    importantInfoText: "Los colores presentados son referencias, en texturas naturales pueden tener diferencias en la intensidad de algunos colores, recomendamos antes de realizar un pedido adquirir una muestra.",
    families: {
      all: "Todos",
      neutral: "Neutros",
      warm: "Cálidos",
      cool: "Fríos",
      metallic: "Metálicos",
    },
    applicationsByFamily: {
      neutral: [
        "Muros monolíticos para residencias de lectura serena.",
        "Vestíbulos y retail con luz uniforme y base limpia.",
        "Interiores donde la textura debe ser la protagonista.",
      ],
      warm: [
        "Hospitality con atmósfera envolvente y sofisticada.",
        "Comedores, lounges y showrooms con mayor calidez material.",
        "Proyectos residenciales combinados con madera o piedra.",
      ],
      cool: [
        "Oficinas boutique y espacios contemporáneos de lectura precisa.",
        "Fachadas o interiores con contraste gráfico controlado.",
        "Escenarios minimalistas con una sensación más técnica.",
      ],
      metallic: [
        "Acentos escultóricos y piezas especiales de alto impacto.",
        "Recepciones premium con brillo controlado.",
        "Detalles protagonistas para hospitality, retail y showrooms.",
      ],
    },
  },
  "es-ES": {
    lineSection: "Colores de línea",
    metallicSection: "Metálicos",
    variationNote: "Debido a su proceso de fabricación neoartesanal las tonalidades expresadas pueden tener variaciones.",
    paletteNote: "Código numérico correspondendiente a la paleta ColorLife® 2.0 de la marca Comex.",
    filterLabel: "Familias",
    selectedLabel: "Selección actual",
    visibleLabel: "tonos visibles",
    paletteLabel: "Referencia de paleta",
    variationLabel: "Variación de tono",
    applicationsLabel: "Información importante",
    importantInfoText: "Los colores presentados son referencias, en texturas naturales pueden tener diferencias en la intensidad de algunos colores, recomendamos antes de realizar un pedido adquirir una muestra.",
    families: {
      all: "Todos",
      neutral: "Neutros",
      warm: "Cálidos",
      cool: "Fríos",
      metallic: "Metálicos",
    },
    applicationsByFamily: {
      neutral: [
        "Muros monolíticos para residencias de lectura serena.",
        "Vestíbulos y retail con luz uniforme y base limpia.",
        "Interiores donde la textura debe ser la protagonista.",
      ],
      warm: [
        "Hospitality con atmósfera envolvente y sofisticada.",
        "Comedores, lounges y showrooms con mayor calidez material.",
        "Proyectos residenciales combinados con madera o piedra.",
      ],
      cool: [
        "Oficinas boutique y espacios contemporáneos de lectura precisa.",
        "Fachadas o interiores con contraste gráfico controlado.",
        "Escenarios minimalistas con una sensación más técnica.",
      ],
      metallic: [
        "Acentos escultóricos y piezas especiales de alto impacto.",
        "Recepciones premium con brillo controlado.",
        "Detalles protagonistas para hospitality, retail y showrooms.",
      ],
    },
  },
  "en-US": {
    lineSection: "Core colors",
    metallicSection: "Metallics",
    variationNote: "Because of the neo-artisanal manufacturing process, the tones shown may vary from piece to piece.",
    paletteNote: "Numeric code corresponding to the ColorLife® 2.0 palette from Comex.",
    filterLabel: "Families",
    selectedLabel: "Current selection",
    visibleLabel: "visible tones",
    paletteLabel: "Palette reference",
    variationLabel: "Tone variation",
    applicationsLabel: "Important information",
    importantInfoText: "The colors shown are references. Natural textures may have differences in the intensity of some tones, so we recommend purchasing a sample before placing an order.",
    families: {
      all: "All",
      neutral: "Neutrals",
      warm: "Warm",
      cool: "Cool",
      metallic: "Metallics",
    },
    applicationsByFamily: {
      neutral: [
        "Monolithic walls for calm residential spaces.",
        "Lobbies and retail environments with clean, even light.",
        "Interiors where texture needs to remain the protagonist.",
      ],
      warm: [
        "Hospitality spaces with an enveloping and refined atmosphere.",
        "Dining rooms, lounges and showrooms with added material warmth.",
        "Residential projects paired with wood or stone.",
      ],
      cool: [
        "Boutique offices and contemporary spaces with precise presence.",
        "Facades or interiors with controlled graphic contrast.",
        "Minimal settings with a more technical feeling.",
      ],
      metallic: [
        "Sculptural accents and high-impact special pieces.",
        "Premium reception areas with controlled shine.",
        "Hero details for hospitality, retail and showroom projects.",
      ],
    },
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].detailPage.colors = {
    ...uiByLocale[locale].detailPage.colors,
    ...colorsPageMetaByLocale[locale],
  };
});

const texturesPageMetaByLocale = {
  "es-MX": {
    applicationsLabel: "Ejemplos de aplicación",
    pairingsLabel: "Sugerencia de combinación",
    colorPairingLabel: "Color sugerido",
    finishPairingLabel: "Acabado sugerido",
  },
  "es-ES": {
    applicationsLabel: "Ejemplos de aplicación",
    pairingsLabel: "Sugerencia de combinación",
    colorPairingLabel: "Color sugerido",
    finishPairingLabel: "Acabado sugerido",
  },
  "en-US": {
    applicationsLabel: "Application examples",
    pairingsLabel: "Suggested pairing",
    colorPairingLabel: "Suggested color",
    finishPairingLabel: "Suggested finish",
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].detailPage.textures = {
    ...uiByLocale[locale].detailPage.textures,
    ...texturesPageMetaByLocale[locale],
  };
});

const finishesPageMetaByLocale = {
  "es-MX": {
    kicker: "Acabados",
    title: "Define el reflejo, la profundidad y la lectura final de cada pieza.",
    description: "El acabado modifica cómo el concreto recibe la luz, cómo se percibe el color y qué atmósfera construye dentro del proyecto.",
    finishLabel: "Acabado seleccionado",
    applicationsLabel: "Información importante",
    importantInfoText: "Las imagenes aquí mostradas son únicamente referencias para ayudar a visualizar las características del pedido. Sin embargo tienen variaciones a la realidad, por lo que te sugerimos a adquirir una muestra antes de realizar la compra.",
    editorialLabel: "Vistas editoriales",
    options: {
      matte: {
        title: "Mate",
        description: "Una lectura calmada y mineral, con reflejo bajo y presencia sobria.",
        applications: ["Muros continuos", "Residencial sereno", "Texturas protagonistas"],
      },
      semiMatte: {
        title: "Semi mate",
        description: "Equilibrio entre suavidad visual y una ligera captura de luz.",
        applications: ["Hospitality boutique", "Retail premium", "Interiores cálidos"],
      },
      gloss: {
        title: "Brillante",
        description: "Mayor definición superficial y una lectura más intensa del color.",
        applications: ["Acentos arquitectónicos", "Piezas de showroom", "Muros de marca"],
      },
      highGloss: {
        title: "Alto brillo",
        description: "Reflejo protagónico para composiciones más gráficas y expresivas.",
        applications: ["Piezas especiales", "Recepciones premium", "Escenas de alto impacto"],
      },
    },
  },
  "es-ES": {
    kicker: "Acabados",
    title: "Define el reflejo, la profundidad y la lectura final de cada pieza.",
    description: "El acabado modifica cómo el hormigón recibe la luz, cómo se percibe el color y qué atmósfera construye dentro del proyecto.",
    finishLabel: "Acabado seleccionado",
    applicationsLabel: "Aplicaciones sugeridas",
    editorialLabel: "Vistas editoriales",
    options: {
      matte: {
        title: "Mate",
        description: "Una lectura calmada y mineral, con reflejo bajo y presencia sobria.",
        applications: ["Muros continuos", "Residencial sereno", "Texturas protagonistas"],
      },
      semiMatte: {
        title: "Semimate",
        description: "Equilibrio entre suavidad visual y una ligera captura de luz.",
        applications: ["Hospitality boutique", "Retail premium", "Interiores cálidos"],
      },
      gloss: {
        title: "Brillante",
        description: "Mayor definición superficial y una lectura más intensa del color.",
        applications: ["Acentos arquitectónicos", "Piezas de showroom", "Muros de marca"],
      },
      highGloss: {
        title: "Alto brillo",
        description: "Reflejo protagonista para composiciones más gráficas y expresivas.",
        applications: ["Piezas especiales", "Recepciones premium", "Escenas de alto impacto"],
      },
    },
  },
  "en-US": {
    kicker: "Finishes",
    title: "Define the reflection, depth and final visual reading of each piece.",
    description: "The finish changes how concrete receives light, how color is perceived and what atmosphere it builds inside the project.",
    finishLabel: "Selected finish",
    applicationsLabel: "Suggested applications",
    editorialLabel: "Editorial views",
    options: {
      matte: {
        title: "Matte",
        description: "A calm mineral reading with low reflection and restrained presence.",
        applications: ["Continuous walls", "Calm residential spaces", "Texture-led surfaces"],
      },
      semiMatte: {
        title: "Semi-matte",
        description: "A balance between visual softness and a subtle capture of light.",
        applications: ["Boutique hospitality", "Premium retail", "Warm interiors"],
      },
      gloss: {
        title: "Gloss",
        description: "Higher surface definition and a more intense reading of color.",
        applications: ["Architectural accents", "Showroom pieces", "Brand walls"],
      },
      highGloss: {
        title: "High gloss",
        description: "Hero reflection for more graphic and expressive compositions.",
        applications: ["Special pieces", "Premium receptions", "High-impact scenes"],
      },
    },
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].detailPage.finishes = finishesPageMetaByLocale[locale];
});

const configuratorPageMetaByLocale = {
  "es-MX": {
    kicker: "Configurador",
    title: "Construye una combinación inicial de textura, color y acabado.",
    description: "Explora una vista previa editorial para entender cómo dialogan las tres capas del sistema material antes de pasar a especificación.",
    previewLabel: "Vista previa",
    summaryLabel: "Selección actual",
    texturesLabel: "1. Elige una textura",
    colorsLabel: "2. Elige un color",
    finishesLabel: "3. Elige un acabado",
    familyLabel: "Familias de color",
    applicationsLabel: "Información importante",
    importantInfoText: "Las imagenes aquí mostradas son únicamente referencias para ayudar a visualizar las características del pedido. Sin embargo tienen variaciones a la realidad, por lo que te sugerimos a adquirir una muestra antes de realizar la compra.",
    contactCta: "Enviar por WhatsApp con esta selección",
  },
  "es-ES": {
    kicker: "Configurador",
    title: "Construye una combinación inicial de textura, color y acabado.",
    description: "Explora una vista previa editorial para entender cómo dialogan las tres capas del sistema material antes de pasar a especificación.",
    previewLabel: "Vista previa",
    summaryLabel: "Selección actual",
    texturesLabel: "1. Elige una textura",
    colorsLabel: "2. Elige un color",
    finishesLabel: "3. Elige un acabado",
    familyLabel: "Familias de color",
    applicationsLabel: "Información importante",
    importantInfoText: "Las imagenes aquí mostradas son únicamente referencias para ayudar a visualizar las características del pedido. Sin embargo tienen variaciones a la realidad, por lo que te sugerimos a adquirir una muestra antes de realizar la compra.",
    contactCta: "Enviar por WhatsApp con esta selección",
  },
  "en-US": {
    kicker: "Configurator",
    title: "Build an initial combination of texture, color and finish.",
    description: "Explore an editorial preview to understand how the three layers of the material system work together before moving into specification.",
    previewLabel: "Preview",
    summaryLabel: "Current selection",
    texturesLabel: "1. Choose a texture",
    colorsLabel: "2. Choose a color",
    finishesLabel: "3. Choose a finish",
    familyLabel: "Color families",
    applicationsLabel: "Important information",
    importantInfoText: "The images shown here are only references to help visualize the characteristics of the order. However, they may vary from reality, so we suggest acquiring a sample before making the purchase.",
    contactCta: "Send via WhatsApp with this selection",
  },
};

Object.keys(uiByLocale).forEach((locale) => {
  uiByLocale[locale].configuratorPage = configuratorPageMetaByLocale[locale];
});

const contactByLocale = {
  "es-MX": {
    region_label: "México y LATAM",
    main_email: "info@aristamasvertice.com",
    support_email: "info@aristaconcreto.com",
    office_phone: "+52 55 7261 6141 / 55 7261 6149",
    sales_whatsapp: "+52 55 7991 7543",
    support_whatsapp: "+52 55 4806 6553",
    header_cta_label: "Contactar",
    header_cta_href: "https://wa.me/525579917543",
    footer_showroom_name: "Showroom CDMX",
    sales_label: "Ventas",
    support_label: "Gerencia",
    regions: [
      { id: "region-mexico", title: "México y LATAM", email: "info@aristamasvertice.com", whatsapp: "+52 55 7991 7543" },
      { id: "region-espana", title: "España", email: "hola@aristamasvertice.es", whatsapp: "+34 621 327 697" },
      { id: "region-usa", title: "Estados Unidos", email: "usa@aristamasvertice.com", whatsapp: "Disponible por correo" },
    ],
    showroom: {
      title: "Showroom CDMX",
      description: "Un espacio para revisar el material en persona, comparar tonos, entender texturas y aterrizar decisiones de proyecto.",
      address: "Álvaro Obregón 242, Roma Norte, Cuauhtémoc, Ciudad de México, 06700",
      hours: "Atención con cita previa",
      map_url:
        "https://www.google.com/maps?q=%C3%81lvaro+Obreg%C3%B3n+242+Roma+Norte+Cuauht%C3%A9moc+Ciudad+de+M%C3%A9xico+06700",
      has_map: true,
    },
  },
  "es-ES": {
    region_label: "España",
    main_email: "hola@aristamasvertice.es",
    support_email: null,
    office_phone: null,
    sales_whatsapp: "+34 621 327 697",
    support_whatsapp: null,
    header_cta_label: "Contactar",
    header_cta_href: "https://wa.me/34621327697",
    footer_showroom_name: "Atención comercial España",
    sales_label: "WhatsApp España",
    support_label: null,
    regions: [
      { id: "region-mexico", title: "México y LATAM", email: "info@aristamasvertice.com", whatsapp: "+52 55 7991 7543" },
      { id: "region-espana", title: "España", email: "hola@aristamasvertice.es", whatsapp: "+34 621 327 697" },
      { id: "region-usa", title: "Estados Unidos", email: "usa@aristamasvertice.com", whatsapp: "Disponible por correo" },
    ],
    showroom: {
      title: "Atención España",
      description: "Acompañamiento comercial, muestras y seguimiento remoto para proyectos en España.",
      address: "Asesoría, muestras y seguimiento remoto para proyectos en España.",
      hours: "Atención con cita previa virtual",
      map_url: null,
      has_map: false,
    },
  },
  "en-US": {
    region_label: "United States",
    main_email: "usa@aristamasvertice.com",
    support_email: null,
    office_phone: null,
    sales_whatsapp: null,
    support_whatsapp: null,
    header_cta_label: "Email us",
    header_cta_href: "mailto:usa@aristamasvertice.com",
    footer_showroom_name: "United States support",
    sales_label: "Commercial email",
    support_label: null,
    regions: [
      { id: "region-mexico", title: "Mexico & LATAM", email: "info@aristamasvertice.com", whatsapp: "+52 55 7991 7543" },
      { id: "region-espana", title: "Spain", email: "hola@aristamasvertice.es", whatsapp: "+34 621 327 697" },
      { id: "region-usa", title: "United States", email: "usa@aristamasvertice.com", whatsapp: "Available by email" },
    ],
    showroom: {
      title: "United States support",
      description: "Commercial advisory and sample coordination for projects in the United States.",
      address: "Commercial advisory and sample coordination for projects in the United States.",
      hours: "Available by email",
      map_url: null,
      has_map: false,
    },
  },
};

const contentByLocale = {
  "es-MX": {
    brand: {
      headline: "Acabados neoartesanales de concreto para proyectos de alta gama.",
      subheadline: "",
      cta_label: "Conocer colecciones",
      secondary_cta_label: "Visitar showroom",
      story: [
        "En Arista + Vértice, cada pieza se produce individualmente para asegurar un acabado con carácter, detalle y autenticidad material.",
        "Trabajamos con formulaciones de concreto desarrolladas específicamente para conservar sus mejores cualidades estructurales y, al mismo tiempo, elevar su expresión estética.",
        "Esto nos permite ofrecer piezas con gran riqueza de textura, variaciones sutiles que hacen única cada unidad, y una estética arquitectónica contemporánea difícil de replicar en procesos estandarizados.",
        "Nuestro proceso combina tecnología, diseño y manufactura artesanal para crear recubrimientos de alto valor visual, pensados para proyectos que buscan diferenciación, durabilidad y sofisticación material.",
      ],
    },
    collections: {
      celosias: { title: getCollectionNaming("es-MX", "celosias").title, description: "Piezas que modulan la luz, crean privacidad y convierten la fachada en un gesto arquitectónico.", applications: ["Fachadas", "Privacidad", "Ventilación"] },
      muro: { title: getCollectionNaming("es-MX", "muro").title, description: "Recubrimientos con lectura mineral y escala arquitectónica para espacios residenciales y comerciales.", applications: ["Interior", "Exterior", "Acentos"] },
      paneles: { title: getCollectionNaming("es-MX", "paneles").title, description: "Formatos protagonistas para crear superficies limpias, continuas y contemporáneas.", applications: ["Fachadas", "Interior", "Exterior"] },
      pisos: { title: getCollectionNaming("es-MX", "pisos").title, description: "Soluciones de alto desempeño con apariencia monolítica y una presencia sobria.", applications: ["Tránsito frecuente", "Interior", "Exterior", "Ambientes premium"] },
    },
    customization: {
      title: "Completa tu pedido en cuatro pasos",
      description: "Sigue esta secuencia para definir diseño, color, textura y acabado de tu proyecto.",
      items: {
        "custom-diseno": { title: "Diseño", description: "Elige el diseño de tu tendencia perfecta para tu proyecto." },
        "custom-color": { title: "Color", description: "Elige el color o colores para tu proyecto." },
        "custom-textura": { title: "Texturas", description: "Elige la estética superficial de las piezas." },
        "custom-acabado": { title: "Acabado", description: "Elige entre mate, semi mate, brillante y alto brillo." },
      },
    },
    allies: {
      title: "Diseñado para arquitectos, interioristas y desarrolladores.",
      description: "Trabajamos de la mano con equipos que valoran el detalle, la consistencia material y la capacidad de personalización.",
      pillars: ["Especificación clara", "Acompañamiento comercial", "Muestras y asesoría", "Atención regional"],
    },
  },
  "es-ES": {
    brand: {
      headline: "Acabados neoartesanales de hormigón para proyectos de alta gama.",
      subheadline: "",
      cta_label: "Ver colecciones",
      secondary_cta_label: "Hablar con asesor",
      story: [
        "En Arista + Vértice, cada pieza se produce individualmente para asegurar un acabado con carácter, detalle y autenticidad material.",
        "Trabajamos con formulaciones de hormigón desarrolladas específicamente para conservar sus mejores cualidades estructurales y, al mismo tiempo, elevar su expresión estética.",
        "Esto nos permite ofrecer piezas con gran riqueza de textura, variaciones sutiles que hacen única cada unidad y una estética arquitectónica contemporánea difícil de replicar en procesos estandarizados.",
        "Nuestro proceso combina tecnología, diseño y manufactura artesanal para crear revestimientos de alto valor visual, pensados para proyectos que buscan diferenciación, durabilidad y sofisticación material.",
      ],
    },
    collections: {
      celosias: { title: getCollectionNaming("es-ES", "celosias").title, description: "Piezas que modulan la luz, crean privacidad y convierten la fachada en un gesto arquitectónico.", applications: ["Fachadas", "Privacidad", "Ventilación"] },
      muro: { title: getCollectionNaming("es-ES", "muro").title, description: "Revestimientos con lectura mineral y escala arquitectónica para espacios residenciales y comerciales.", applications: ["Interior", "Exterior", "Acentos"] },
      paneles: { title: getCollectionNaming("es-ES", "paneles").title, description: "Formatos protagonistas para crear superficies limpias, continuas y contemporáneas.", applications: ["Fachadas", "Interior", "Exterior"] },
      pisos: { title: getCollectionNaming("es-ES", "pisos").title, description: "Soluciones de alto desempeño con apariencia monolítica y una presencia sobria.", applications: ["Tránsito frecuente", "Interior", "Exterior", "Ambientes premium"] },
    },
    customization: {
      title: "Completa tu pedido en cuatro pasos",
      description: "Sigue esta secuencia para definir diseño, color, textura y acabado de tu proyecto.",
      items: {
        "custom-diseno": { title: "Diseño", description: "Elige el diseño de tendencia perfecto para tu proyecto." },
        "custom-color": { title: "Color", description: "Elige el color o colores para tu proyecto." },
        "custom-textura": { title: "Texturas", description: "Elige la estética superficial de las piezas." },
        "custom-acabado": { title: "Acabado", description: "Elige entre mate, semimate, brillante y alto brillo." },
      },
    },
    allies: {
      title: "Diseñado para arquitectos, interioristas y promotores.",
      description: "Trabajamos junto a equipos que valoran el detalle, la consistencia material y la capacidad de personalización.",
      pillars: ["Especificación clara", "Acompañamiento comercial", "Muestras y asesoría", "Atención regional"],
    },
  },
  "en-US": {
    brand: {
      headline: "Neo-artisanal concrete finishes for high-end projects.",
      subheadline: "",
      cta_label: "Explore collections",
      secondary_cta_label: "Speak with our team",
      story: [
        "At Arista + Vértice, every piece is produced individually to ensure a finish with character, detail and material authenticity.",
        "We work with concrete formulations specifically developed to preserve the material's best structural qualities while elevating its aesthetic expression.",
        "This allows us to offer pieces with rich texture, subtle variations that make every unit unique, and a contemporary architectural presence that is difficult to replicate through standardized processes.",
        "Our process combines technology, design and craft manufacturing to create high-value architectural surfaces for projects seeking distinction, durability and material sophistication.",
      ],
    },
    collections: {
      celosias: { title: getCollectionNaming("en-US", "celosias").title, description: "Pieces that modulate light, create privacy and turn a façade into an architectural gesture.", applications: ["Facades", "Privacy", "Ventilation"] },
      muro: { title: getCollectionNaming("en-US", "muro").title, description: "Architectural wall surfaces with a mineral reading for residential and commercial spaces.", applications: ["Interior", "Exterior", "Accents"] },
      paneles: { title: getCollectionNaming("en-US", "paneles").title, description: "Statement formats that build clean, continuous and contemporary surfaces.", applications: ["Facades", "Interior", "Exterior"] },
      pisos: { title: getCollectionNaming("en-US", "pisos").title, description: "High-performance solutions with a monolithic look and understated presence.", applications: ["High traffic", "Interior", "Exterior", "Premium spaces"] },
    },
    customization: {
      title: "Complete your order in four steps",
      description: "Follow this sequence to define the design, color, texture and finish of your project.",
      items: {
        "custom-diseno": { title: "Design", description: "Choose the design direction that best fits your project." },
        "custom-color": { title: "Color", description: "Choose the color or color palette for your project." },
        "custom-textura": { title: "Textures", description: "Choose the surface expression of each piece." },
        "custom-acabado": { title: "Finish", description: "Choose between matte, semi-matte, gloss and high-gloss." },
      },
    },
    allies: {
      title: "Designed for architects, interior designers and developers.",
      description: "We collaborate with teams that value detail, material consistency and a high level of customization.",
      pillars: ["Clear specification", "Commercial guidance", "Samples and advisory", "Regional support"],
    },
  },
};

const contentExtensionsByLocale = {
  "es-MX": {
    manuals: {
      "manual-descarga-general": {
        title: "Manual de descarga de losetas",
        description: "Guía general para descarga, maniobra y preparación básica de losetas en obra.",
        label: "Ver manual",
      },
      "manual-instalacion-losetas": {
        title: "Instalación de losetas",
        description: "Guía base para instalación de losetas y buenas prácticas de aplicación en obra.",
        label: "Ver manual",
      },
      "manual-descarga-paneles": {
        title: "Manual de descarga de paneles",
        description: "Indicaciones específicas para descarga y manejo seguro de paneles en sitio.",
        label: "Ver manual",
      },
      "manual-descarga-celosias-paneles": {
        title: "Manual de descarga de paneles de celosías",
        description: "Proceso de maniobra y recomendaciones para muros divisorios y piezas modulares especiales.",
        label: "Ver manual",
      },
      "manual-instalacion-celosias": {
        title: "Manual de instalación de celosías",
        description: "Proceso base de instalación para celosías y muros divisorios, asegurando un montaje correcto en obra.",
        label: "Ver manual",
      },
      "manual-limpieza-mantenimiento": {
        title: "Manual de limpieza y mantenimiento",
        description: "Criterios de limpieza, conservación y cuidado para mantener la apariencia del material.",
        label: "Ver manual",
      },
    },
  },
  "es-ES": {
    hero_slides: {
      "hero-1": { alt: "Aplicación arquitectónica de hormigón Arista + Vértice" },
      "hero-2": { alt: "Interiorismo con acabado premium de hormigón" },
      "hero-3": { alt: "Textura escultórica de hormigón para muro" },
      "hero-4": { alt: "Composición minimalista con revestimientos de hormigón" },
      "hero-5": { alt: "Producto de hormigón de alta gama para interiorismo" },
      "hero-6": { alt: "Superficie arquitectónica con carácter neoartesanal" },
    },
    manuals: {
      "manual-descarga-general": {
        title: "Manual de descarga de losetas",
        description: "Guía general para descarga, maniobra y preparación básica de losetas en obra.",
        label: "Ver manual",
      },
      "manual-instalacion-losetas": {
        title: "Instalación de losetas",
        description: "Guía base para instalación de losetas y buenas prácticas de aplicación en obra.",
        label: "Ver manual",
      },
      "manual-descarga-paneles": {
        title: "Manual de descarga de paneles",
        description: "Indicaciones específicas para descarga y manejo seguro de paneles en obra.",
        label: "Ver manual",
      },
      "manual-descarga-celosias-paneles": {
        title: "Manual de descarga de paneles de celosías",
        description: "Proceso de maniobra y recomendaciones para muros divisorios y piezas modulares especiales.",
        label: "Ver manual",
      },
      "manual-instalacion-celosias": {
        title: "Manual de instalación de celosías",
        description: "Proceso base de instalación para celosías y muros divisorios, asegurando un montaje correcto en obra.",
        label: "Ver manual",
      },
      "manual-limpieza-mantenimiento": {
        title: "Manual de limpieza y mantenimiento",
        description: "Criterios de limpieza, conservación y cuidado para mantener la apariencia del material.",
        label: "Ver manual",
      },
    },
  },
  "en-US": {
    hero_slides: {
      "hero-1": { alt: "Architectural concrete application by Arista + Vértice" },
      "hero-2": { alt: "Interior design with a premium concrete finish" },
      "hero-3": { alt: "Sculptural concrete wall texture" },
      "hero-4": { alt: "Minimal composition with concrete surfaces" },
      "hero-5": { alt: "High-end concrete product for interior design" },
      "hero-6": { alt: "Architectural surface with neo-artisanal character" },
    },
    catalogs: {
      "catalogo-2026": { title: "2025 Collections Catalog", description: "English edition of the Arista + Vértice collections catalog.", label: "Open catalog" },
      "catalogo-lienzo": { title: "Lienzo", description: "Editorial cover for the Lienzo collection.", label: "Open catalog" },
      "catalogo-minimal": { title: "Minimal", description: "Editorial cover for the Minimal collection.", label: "Open catalog" },
      "catalogo-nouvo": { title: "Nouvó", description: "Editorial cover for the Nouvó collection.", label: "Open catalog" },
      "catalogo-relieve": { title: "Relieve", description: "Editorial cover for the Relieve collection.", label: "Open catalog" },
      "catalogo-vitro": { title: "Vitro", description: "Editorial cover for the Vitro collection.", label: "Open catalog" },
      "catalogo-icnita": { title: "Icnita", description: "Editorial cover for the Icnita collection.", label: "Open catalog" },
      "catalogo-lite": { title: "Lite", description: "Editorial cover for the Lite collection.", label: "Open catalog" },
      "catalogo-paneles": { title: getCollectionNaming("en-US", "paneles").title, description: `Editorial cover for the ${getCollectionNaming("en-US", "paneles").title} collection.`, label: "Open catalog" },
      "catalogo-plank": { title: "Plank", description: "Editorial cover for the Plank collection.", label: "Open catalog" },
    },
    textures: {
      "textura-liso": { title: "Smooth", description: "A low porosity percentage gives the piece a smooth appearance.", applications: ["Continuous walls", "Calm residential spaces", "Bathrooms and spa areas"], recommended_color: "Light neutrals", recommended_finish: "Matte" },
      "textura-aparente": { title: "Exposed", description: "It presents the porosity of natural concrete, while the color remains homogeneous across the piece.", applications: ["Restrained facades", "Premium retail", "Warm interiors"], recommended_color: "Medium warm tones", recommended_finish: "Semi-matte" },
      "textura-natural": { title: "Natural", description: "It presents the porosity of natural concrete and the color is not homogeneous across the piece, creating variations between each one.", applications: ["Warm hospitality", "Accent walls", "Residential settings with wood"], recommended_color: "Neutrals and warm tones", recommended_finish: "Matte" },
      "textura-recinto": { title: "Recinto", description: "It presents saturated porosity and the pore diameter is larger than in the <Exposed texture>, resembling recinto stone.", applications: ["Sculptural exteriors", "Landscape features", "Divider walls"], recommended_color: "Cool and neutral tones", recommended_finish: "Semi-matte" },
      "textura-piedra-volcanica": { title: "Volcanic stone", description: "The surface of the pieces in this texture shows wear, losing definition and generating a more rustic piece.", applications: ["Character-rich facades", "Transition spaces", "Rustic pieces"], recommended_color: "Dark and cool tones", recommended_finish: "Matte" },
      "textura-marmoleado": { title: "Marbled", description: "A marbled texture in concrete is obtained by incorporating patterns and veins similar to those found in marble.", applications: ["Premium receptions", "Special pieces", "Showrooms"], recommended_color: "Metallic and cool tones", recommended_finish: "Gloss" },
    },
    colors: {
      "marfil-mineral": { title: "Mineral ivory", description: "Ideal for luminous compositions and spaces with a warm tone." },
      "arena-caliza": { title: "Limestone sand", description: "An architectural neutral for sober and contemporary projects." },
      "gris-niebla": { title: "Mist gray", description: "A balanced tone between visual softness and material presence." },
      "grafito-suave": { title: "Soft graphite", description: "Adds refined contrast without making the atmosphere feel harsh." },
      "basalto-profundo": { title: "Deep basalt", description: "A more intense option for high-contrast scenes and discreet luxury." },
      "terracota-minima": { title: "Minimal terracotta", description: "A warm tone with sculptural character for expressive projects." },
    },
    manuals: {
      "manual-descarga-general": {
        title: "Tile unloading manual",
        description: "Guidelines for handling, unloading and receiving tile applications on site.",
        label: "View manual",
      },
      "manual-instalacion-losetas": {
        title: "Floor installation manual",
        description: "Guidelines for installation, handling and care of floor applications.",
        label: "View manual",
      },
      "manual-descarga-paneles": {
        title: `${getCollectionNaming("en-US", "paneles").title} unloading manual`,
        description: `Specific handling and unloading guidance for ${getCollectionNaming("en-US", "paneles").designPluralLabel} on site.`,
        label: "View manual",
      },
      "manual-descarga-celosias-paneles": {
        title: `${getCollectionNaming("en-US", "celosias").title} unloading manual`,
        description: `Handling and unloading guidance for ${getCollectionNaming("en-US", "celosias").designPluralLabel} and divider wall systems.`,
        label: "View manual",
      },
      "manual-instalacion-celosias": {
        title: `${getCollectionNaming("en-US", "celosias").title} installation manual`,
        description: `Base installation process for ${getCollectionNaming("en-US", "celosias").designPluralLabel} and divider walls to ensure a correct on-site assembly.`,
        label: "View manual",
      },
      "manual-limpieza-mantenimiento": {
        title: "Cleaning and maintenance manual",
        description: "Cleaning, conservation and care criteria to preserve the material appearance.",
        label: "View manual",
      },
    },
  },
};

Object.keys(contentExtensionsByLocale).forEach((locale) => {
  contentByLocale[locale] = {
    ...contentByLocale[locale],
    ...contentExtensionsByLocale[locale],
  };
});

Object.keys(contentByLocale).forEach((locale) => {
  const localizedCollections = contentByLocale[locale]?.collections;

  if (!localizedCollections) {
    return;
  }

  contentByLocale[locale] = {
    ...contentByLocale[locale],
    collections: Object.fromEntries(
      Object.entries(localizedCollections).map(([collectionId, collectionContent]) => [
        collectionId,
        {
          ...collectionContent,
          title: getCollectionNaming(locale, collectionId).title,
        },
      ]),
    ),
  };
});

const SHARED_RESOURCE_PDF_URL = "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/4kcttk8l_Colecciones%202026%20-%20ARISTA%2BVE%CC%81RTICE%20%28act%2020_01_2026%29.pdf";
const UNIVERSAL_RESOURCE_COLLECTIONS = ["celosias", "muro", "paneles", "pisos"];

const resourceUrlsById = {
  catalogs: {
    "catalogo-2026": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/4kcttk8l_Colecciones%202026%20-%20ARISTA%2BVE%CC%81RTICE%20%28act%2020_01_2026%29.pdf",
    "catalogo-lienzo": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/1z546pav_LIENZO.pdf",
    "catalogo-minimal": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/984i6fak_MINIMAL.pdf",
    "catalogo-nouvo": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/x6lbhr7j_NOUVO%CC%81.pdf",
    "catalogo-relieve": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/tp1phc1m_RELIEVE.pdf",
    "catalogo-vitro": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/hmzvuok1_VITRO.pdf",
    "catalogo-icnita": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/yhxkealv_PANELES_ICNITA.pdf",
    "catalogo-lite": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/p5jousld_PANELES_LITE.pdf",
    "catalogo-paneles": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/y42todtg_PANELES%20.pdf",
    "catalogo-plank": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/e8s7zme6_PANELES_PLANK.pdf",
  },
  manuals: {
    "manual-descarga-general": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/n8fabfbv_Manual-de-descarga.pdf",
    "manual-instalacion-losetas": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/2vf80nz9_Manual_Floor%20US.pdf",
    "manual-descarga-paneles": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/17dy83oy_Manual-de-descarga-Paneles-Arista-Vertice.pdf",
    "manual-descarga-celosias-paneles": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j1gqiwwd_Manual-de-descarga-Celosias-Paneles-Arista-Vertice.pdf",
    "manual-instalacion-celosias": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/v77ww402_ManualBreezeBlocks.pdf",
    "manual-limpieza-mantenimiento": "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/511k9cqw_Limpieza-y-mantenimiento.pdf",
  },
};

const resourceCollectionsById = {
  catalogs: {
    "catalogo-2026": UNIVERSAL_RESOURCE_COLLECTIONS,
    "catalogo-lienzo": ["muro"],
    "catalogo-minimal": ["muro"],
    "catalogo-nouvo": ["muro"],
    "catalogo-relieve": ["muro"],
    "catalogo-vitro": ["muro"],
    "catalogo-icnita": ["paneles"],
    "catalogo-lite": ["paneles"],
    "catalogo-paneles": ["paneles"],
    "catalogo-plank": ["paneles"],
  },
  manuals: {
    "manual-descarga-general": UNIVERSAL_RESOURCE_COLLECTIONS,
    "manual-instalacion-losetas": UNIVERSAL_RESOURCE_COLLECTIONS,
    "manual-descarga-paneles": ["paneles"],
    "manual-descarga-celosias-paneles": ["celosias", "paneles"],
    "manual-instalacion-celosias": ["celosias"],
    "manual-limpieza-mantenimiento": UNIVERSAL_RESOURCE_COLLECTIONS,
  },
};

const localizedResourceOverridesByLocale = {
  "es-MX": {
    manuals: {
      "manual-instalacion-losetas": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j48ru2rh_Manual%20de%20Instalacio%CC%81n%20de%20Pisos.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j48ru2rh_Manual%20de%20Instalacio%CC%81n%20de%20Pisos.pdf",
        collection_tags: UNIVERSAL_RESOURCE_COLLECTIONS,
      },
      "manual-instalacion-celosias": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/dme5sg4a_Manual%20de%20Instalacio%CC%81n%20de%20Celosi%CC%81as.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/dme5sg4a_Manual%20de%20Instalacio%CC%81n%20de%20Celosi%CC%81as.pdf",
        collection_tags: ["celosias"],
      },
    },
    manualsExtra: [
      {
        id: "manual-instalacion-losetas-paneles",
        title: "Instalación de losetas y paneles",
        description: "Guía de instalación para losetas y paneles con recomendaciones de aplicación y montaje en obra.",
        label: "Ver manual",
        href: "/manuales",
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/ldr7vxtb_Manual%20de%20Instalacio%CC%81n%20Losetas%20y%20Paneles.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/ldr7vxtb_Manual%20de%20Instalacio%CC%81n%20Losetas%20y%20Paneles.pdf",
        collection_tags: ["muro", "paneles"],
      },
    ],
  },
  "es-ES": {
    manuals: {
      "manual-instalacion-losetas": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j48ru2rh_Manual%20de%20Instalacio%CC%81n%20de%20Pisos.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j48ru2rh_Manual%20de%20Instalacio%CC%81n%20de%20Pisos.pdf",
        collection_tags: UNIVERSAL_RESOURCE_COLLECTIONS,
      },
      "manual-instalacion-celosias": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/dme5sg4a_Manual%20de%20Instalacio%CC%81n%20de%20Celosi%CC%81as.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/dme5sg4a_Manual%20de%20Instalacio%CC%81n%20de%20Celosi%CC%81as.pdf",
        collection_tags: ["celosias"],
      },
    },
    manualsExtra: [
      {
        id: "manual-instalacion-losetas-paneles",
        title: "Instalación de losetas y paneles",
        description: "Guía de instalación para losetas y paneles con recomendaciones de aplicación y montaje en obra.",
        label: "Ver manual",
        href: "/manuales",
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/ldr7vxtb_Manual%20de%20Instalacio%CC%81n%20Losetas%20y%20Paneles.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/ldr7vxtb_Manual%20de%20Instalacio%CC%81n%20Losetas%20y%20Paneles.pdf",
        collection_tags: ["muro", "paneles"],
      },
    ],
  },
  "en-US": {
    catalogs: {
      "catalogo-2026": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/jbm5at7d_2025%20ARISTA%2BVE%CC%81RTICE%202025%20Collections.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/jbm5at7d_2025%20ARISTA%2BVE%CC%81RTICE%202025%20Collections.pdf",
      },
    },
    manuals: {
      "manual-descarga-general": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/n8fabfbv_Manual-de-descarga.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/n8fabfbv_Manual-de-descarga.pdf",
        collection_tags: UNIVERSAL_RESOURCE_COLLECTIONS,
      },
      "manual-instalacion-losetas": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/2vf80nz9_Manual_Floor%20US.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/2vf80nz9_Manual_Floor%20US.pdf",
        collection_tags: UNIVERSAL_RESOURCE_COLLECTIONS,
      },
      "manual-descarga-paneles": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/17dy83oy_Manual-de-descarga-Paneles-Arista-Vertice.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/17dy83oy_Manual-de-descarga-Paneles-Arista-Vertice.pdf",
        collection_tags: ["paneles"],
      },
      "manual-descarga-celosias-paneles": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j1gqiwwd_Manual-de-descarga-Celosias-Paneles-Arista-Vertice.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/j1gqiwwd_Manual-de-descarga-Celosias-Paneles-Arista-Vertice.pdf",
        collection_tags: ["celosias"],
      },
      "manual-instalacion-celosias": {
        resource_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/v77ww402_ManualBreezeBlocks.pdf",
        download_url: "https://customer-assets.emergentagent.com/job_luxury-concrete-1/artifacts/v77ww402_ManualBreezeBlocks.pdf",
        collection_tags: ["celosias"],
      },
    },
  },
};

export const getLocaleUi = (locale) => uiByLocale[locale] || uiByLocale["es-MX"];

export const getLocalizedSiteBundle = (rawData, locale) => {
  const ui = cloneValue(getLocaleUi(locale));
  const region = contactByLocale[locale] || contactByLocale["es-MX"];
  const content = contentByLocale[locale] || contentByLocale["es-MX"];
  const data = cloneValue(rawData || {});
  const mapLocalizedItems = (items, translations = {}) => items.map((item) => ({ ...item, ...(translations[item.id] || {}) }));
  const enrichResources = (items, type) =>
    items.map((item) => ({
      ...item,
      resource_url: item.resource_url || resourceUrlsById[type]?.[item.id] || SHARED_RESOURCE_PDF_URL,
      download_url: item.download_url || resourceUrlsById[type]?.[item.id] || SHARED_RESOURCE_PDF_URL,
      collection_tags: item.collection_tags || resourceCollectionsById[type]?.[item.id] || UNIVERSAL_RESOURCE_COLLECTIONS,
    }));

  data.navigation = data.navigation.map((item, index) => ({
    ...item,
    label: item.useCmsLabel ? (item.label || ui.navigation[index] || item.label) : (ui.navigation[index] || item.label),
  }));
  data.brand = { ...data.brand, ...content.brand };
  data.hero_slides = mapLocalizedItems(data.hero_slides, content.hero_slides);
  data.collections = mapLocalizedItems(data.collections, content.collections);
  data.catalogs = enrichResources(mapLocalizedItems(data.catalogs, content.catalogs), "catalogs");
  data.textures = mapLocalizedItems(data.textures, content.textures);
  data.colors = mapLocalizedItems(data.colors, content.colors);
  data.manuals = enrichResources(mapLocalizedItems(data.manuals, content.manuals), "manuals");

  const localeCatalogOverrides = localizedResourceOverridesByLocale[locale]?.catalogs;
  if (localeCatalogOverrides) {
    data.catalogs = data.catalogs.map((item) => (localeCatalogOverrides[item.id] ? { ...item, ...localeCatalogOverrides[item.id] } : item));
  }

  const localeManualOverrides = localizedResourceOverridesByLocale[locale]?.manuals;
  if (localeManualOverrides) {
    data.manuals = data.manuals
      .map((item) => ({ ...item, ...(localeManualOverrides[item.id] || {}) }));
  }

  const localeManualExtras = localizedResourceOverridesByLocale[locale]?.manualsExtra || [];
  if (localeManualExtras.length) {
    const existingIds = new Set(data.manuals.map((item) => item.id));
    data.manuals = [
      ...data.manuals,
      ...localeManualExtras.filter((item) => !existingIds.has(item.id)),
    ];
  }

  data.customization = {
    ...data.customization,
    title: content.customization.title,
    description: content.customization.description,
    items: data.customization.items.map((item) => ({ ...item, ...(content.customization.items[item.id] || {}) })),
  };
  data.allies = { ...data.allies, ...content.allies };
  data.showroom = { ...data.showroom, ...region.showroom };
  data.contact = { ...data.contact, ...region };
  data.homeAssets = { ...DEFAULT_HOME_ASSETS, ...(data.homeAssets || {}) };

  const pageContentByLocale = rawData?.page_content?.[locale] || {};
  const nextState = Object.values(pageContentByLocale).reduce((accumulator, overrideDocument) => {
    if (!isPlainObject(overrideDocument)) {
      return accumulator;
    }

    return {
      data: isPlainObject(overrideDocument.data)
        ? mergeLocalizedOverride(accumulator.data, overrideDocument.data)
        : accumulator.data,
      ui: isPlainObject(overrideDocument.ui)
        ? mergeLocalizedOverride(accumulator.ui, overrideDocument.ui)
        : accumulator.ui,
    };
  }, { data, ui });

  nextState.data.homeAssets = { ...DEFAULT_HOME_ASSETS, ...(nextState.data.homeAssets || {}) };

  return nextState;
};

export const getLocalizedSiteData = (rawData, locale) => getLocalizedSiteBundle(rawData, locale).data;